import { useCallback } from 'react'
import createPersistedState from '../usePersistedState'

const IMPERSONATION_HISTORY_KEY = 'impersonation_history'
const MAX_HISTORY_ITEMS = 10

// Create a persisted state hook for impersonation history
const useImpersonationHistoryState = createPersistedState(
  IMPERSONATION_HISTORY_KEY
)

interface UseImpersonationHistoryReturn {
  history: string[]
  addEmail: (email: string) => void
  removeEmail: (email: string) => void
  clearHistory: () => void
}

/**
 * Hook to manage impersonation history in localStorage
 * Stores up to 10 recent impersonation emails
 */
export const useImpersonationHistory = (): UseImpersonationHistoryReturn => {
  const [history, setHistory] = useImpersonationHistoryState([])

  const addEmail = useCallback(
    (email: string) => {
      if (!email || typeof email !== 'string') {
        return
      }

      const trimmedEmail = email.trim().toLowerCase()

      if (!trimmedEmail) {
        return
      }

      setHistory((prevHistory: string[]) => {
        // Remove the email if it already exists to avoid duplicates
        const filteredHistory = prevHistory.filter(
          (item: string) => item !== trimmedEmail
        )

        // Add the new email to the beginning and limit to MAX_HISTORY_ITEMS
        const newHistory = [trimmedEmail, ...filteredHistory].slice(
          0,
          MAX_HISTORY_ITEMS
        )

        return newHistory
      })
    },
    [setHistory]
  )

  const removeEmail = useCallback(
    (email: string) => {
      if (!email || typeof email !== 'string') {
        return
      }

      const trimmedEmail = email.trim().toLowerCase()

      if (!trimmedEmail) {
        return
      }

      setHistory((prevHistory: string[]) => {
        return prevHistory.filter((item: string) => item !== trimmedEmail)
      })
    },
    [setHistory]
  )

  const clearHistory = useCallback(() => {
    setHistory([])
  }, [setHistory])

  return {
    history,
    addEmail,
    removeEmail,
    clearHistory
  }
}

export default useImpersonationHistory
